import { COLORS, FONTS } from "../../../lib/brand";
import { PANE_THEMES } from "../../../components/brand/paneThemes";
import type { Href } from "expo-router";
import { router, useFocusEffect } from "expo-router";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  ActivityIndicator,
  Linking,
  Modal,
  Platform,
  Pressable,
  ScrollView,
  Text,
  TextInput,
  View,
  type TextStyle,
  type ViewStyle,
} from "react-native";

import { listAllCompanies, listAllCompetitions, type AdminCompanyListRow, type AdminCompetitionListRow } from "../../../lib/admin";
import {
  bucketForDate,
  createCall,
  linkCallToProgram,
  listPastCalls,
  listUpcomingCalls,
  updateCall,
  updateCallStatus,
  type CoravuCallOutcome,
  type CoravuCallRow,
  type DayBucket,
} from "../../../lib/coravuCalls";
import type { CoravuCallStatus } from "../../../types/database";
import {
  AdminButton,
  AdminError,
  AdminInput,
  AdminLoading,
  AdminPageHeader,
} from "../../../components/admin/ui";

const BOOKING_URL = "https://outlook.office.com/book/CoravuIntake@Coravu.com/";

const OUTCOME_OPTIONS: { value: CoravuCallOutcome; label: string }[] = [
  { value: "sent_program", label: "Sent program" },
  { value: "followup_needed", label: "Followup needed" },
  { value: "not_a_fit", label: "Not a fit" },
  { value: "ghosted", label: "Ghosted" },
];

function outcomeLabel(o: CoravuCallOutcome | null | undefined): string {
  if (!o) return "—";
  return OUTCOME_OPTIONS.find((x) => x.value === o)?.label ?? o;
}

const THEME = PANE_THEMES.Admin;

const BLACK = THEME.pageBg;
const FOREST_MID = THEME.surfaceRaisedBg;
const FOREST = THEME.sidebarBg;
const AMBER = THEME.accent;
const SAGE = COLORS.SAGE.hex;
const LINEN = THEME.textOnSurface;

const W = "rgba(255,255,255,1)";
const WS = "rgba(255,255,255,0.78)";
const WM = "rgba(255,255,255,0.55)";
const WL = "rgba(255,255,255,0.28)";
const RED = THEME.destructive;
const BORDER = "rgba(255,255,255,0.07)";

const TZ = "America/New_York";

const BUCKET_LABELS: Record<DayBucket, string> = {
  today: "Today",
  tomorrow: "Tomorrow",
  this_week: "This week",
  later: "Later",
};

const BUCKET_ORDER: DayBucket[] = ["today", "tomorrow", "this_week", "later"];

const STATUS_LABEL: Record<CoravuCallStatus, string> = {
  scheduled: "Scheduled",
  completed: "Completed",
  cancelled: "Cancelled",
  no_show: "No-show",
};

const STATUS_TONE: Record<CoravuCallStatus, string> = {
  scheduled: AMBER,
  completed: SAGE,
  cancelled: RED,
  no_show: WL,
};

function fmtTime(iso: string): string {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "—";
  return d.toLocaleTimeString("en-US", {
    hour: "numeric",
    minute: "2-digit",
    timeZone: TZ,
  });
}

function fmtShortDate(iso: string): string {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "—";
  return d.toLocaleDateString("en-US", {
    weekday: "short",
    month: "short",
    day: "numeric",
    timeZone: TZ,
  });
}

function fmtTzAbbr(iso: string): string {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "";
  // Pull just the short TZ abbreviation from a TZ-aware format string.
  const parts = new Intl.DateTimeFormat("en-US", {
    timeZone: TZ,
    timeZoneName: "short",
  }).formatToParts(d);
  return parts.find((p) => p.type === "timeZoneName")?.value ?? "";
}

export default function AdminCalendarScreen() {
  const [upcoming, setUpcoming] = useState<CoravuCallRow[]>([]);
  const [past, setPast] = useState<CoravuCallRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState<string | null>(null);
  const [showPast, setShowPast] = useState(false);
  const [logOpen, setLogOpen] = useState(false);
  const [editCall, setEditCall] = useState<CoravuCallRow | null>(null);
  const [linkCall, setLinkCall] = useState<CoravuCallRow | null>(null);
  const [bookingCopied, setBookingCopied] = useState(false);
  const hasLoadedRef = useRef(false);

  const load = useCallback(async () => {
    if (!hasLoadedRef.current) setLoading(true);
    setErr(null);
    try {
      const [u, p] = await Promise.all([listUpcomingCalls(), listPastCalls(50)]);
      setUpcoming(u);
      setPast(p);
    } catch (e) {
      setErr(e instanceof Error ? e.message : "Failed to load calls");
    } finally {
      hasLoadedRef.current = true;
      setLoading(false);
    }
  }, []);

  useFocusEffect(
    useCallback(() => {
      void load();
    }, [load])
  );

  const grouped = useMemo(() => {
    const out: Record<DayBucket, CoravuCallRow[]> = {
      today: [],
      tomorrow: [],
      this_week: [],
      later: [],
    };
    const now = new Date();
    for (const row of upcoming) {
      const bucket = bucketForDate(new Date(row.starts_at), now);
      out[bucket].push(row);
    }
    return out;
  }, [upcoming]);

  async function onStatus(id: string, status: CoravuCallStatus) {
    const ok = await updateCallStatus(id, status);
    if (!ok) setErr("Could not update call status.");
    await load();
  }

  function openExternal(url: string) {
    if (Platform.OS === "web") {
      if (typeof window !== "undefined") window.open(url, "_blank");
    } else {
      void Linking.openURL(url);
    }
  }

  function copyBookingUrl() {
    if (Platform.OS === "web" && typeof navigator !== "undefined" && navigator.clipboard) {
      void navigator.clipboard.writeText(BOOKING_URL);
    }
    setBookingCopied(true);
    setTimeout(() => setBookingCopied(false), 1600);
  }

  return (
    <ScrollView style={{ flex: 1, backgroundColor: BLACK }} contentContainerStyle={{ paddingBottom: 32 }}>
      <View style={{ padding: 24, maxWidth: 1080 }}>
        <AdminPageHeader
          theme={THEME}
          eyebrow="Overview · Calendar"
          title="Calendar"
          subtitle="Upcoming Coravu calls. Times shown in Eastern."
          actions={<AdminButton theme={THEME} label="+ Log a call" onPress={() => setLogOpen(true)} />}
        />

        {err ? <View style={{ marginBottom: 14 }}><AdminError theme={THEME} message={err} /></View> : null}

        {loading ? (
          <AdminLoading theme={THEME} />
        ) : (
          <View style={{ marginTop: 18 }}>
            {upcoming.length === 0 ? (
              <View
                style={{
                  padding: 22,
                  borderRadius: 6,
                  backgroundColor: FOREST_MID,
                  borderWidth: 1,
                  borderColor: BORDER,
                }}
              >
                <Text style={{ fontFamily: FONTS.displayMedium, fontSize: 18, color: LINEN }}>
                  No upcoming calls.
                </Text>
                <Text style={{ marginTop: 4, fontFamily: FONTS.body, fontSize: 12, color: WM }}>
                  When a discovery call is on the books, it shows up here.
                </Text>
                <View style={{ marginTop: 12, flexDirection: "row", flexWrap: "wrap", gap: 8 }}>
                  <Pressable
                    onPress={() => openExternal(BOOKING_URL)}
                    style={[
                      {
                        paddingHorizontal: 12,
                        paddingVertical: 7,
                        borderRadius: 3,
                        borderWidth: 1,
                        borderColor: AMBER,
                        backgroundColor: "rgba(181,135,58,0.10)",
                      } as ViewStyle,
                      Platform.OS === "web" ? ({ cursor: "pointer" } as const) : null,
                    ]}
                  >
                    <Text style={{ fontFamily: FONTS.bodyBold, fontSize: 10.5, letterSpacing: 1.2, textTransform: "uppercase", color: AMBER }}>
                      Open booking page →
                    </Text>
                  </Pressable>
                  <Pressable
                    onPress={copyBookingUrl}
                    style={[
                      {
                        paddingHorizontal: 12,
                        paddingVertical: 7,
                        borderRadius: 3,
                        borderWidth: 1,
                        borderColor: BORDER,
                        backgroundColor: "rgba(255,255,255,0.02)",
                      } as ViewStyle,
                      Platform.OS === "web" ? ({ cursor: "pointer" } as const) : null,
                    ]}
                  >
                    <Text style={{ fontFamily: FONTS.bodyBold, fontSize: 10.5, letterSpacing: 1.2, textTransform: "uppercase", color: bookingCopied ? AMBER : WS }}>
                      {bookingCopied ? "Copied" : "Copy booking link"}
                    </Text>
                  </Pressable>
                </View>
              </View>
            ) : null}

            {BUCKET_ORDER.map((bucket) => {
              const rows = grouped[bucket];
              if (rows.length === 0) return null;
              return (
                <View key={bucket} style={{ marginBottom: 20 }}>
                  <Text
                    style={{
                      marginBottom: 8,
                      fontFamily: FONTS.bodyBold,
                      fontSize: 9.5,
                      letterSpacing: 2,
                      textTransform: "uppercase",
                      color: WM,
                    }}
                  >
                    {BUCKET_LABELS[bucket]}
                  </Text>
                  <View style={{ gap: 8 }}>
                    {rows.map((row) => (
                      <CallRow
                        key={row.id}
                        row={row}
                        onJoin={() => row.teams_join_url && openExternal(row.teams_join_url)}
                        onAddToCalendar={() => row.ics_url && openExternal(row.ics_url)}
                        onMarkComplete={() => void onStatus(row.id, "completed")}
                        onMarkNoShow={() => void onStatus(row.id, "no_show")}
                        onCancel={() => void onStatus(row.id, "cancelled")}
                        onEdit={() => setEditCall(row)}
                        onLinkProgram={() => setLinkCall(row)}
                      />
                    ))}
                  </View>
                </View>
              );
            })}

            <Pressable
              onPress={() => setShowPast((s) => !s)}
              style={[
                { alignSelf: "flex-start", paddingVertical: 6 } as ViewStyle,
                Platform.OS === "web" ? ({ cursor: "pointer" } as const) : null,
              ]}
            >
              <Text
                style={{
                  fontFamily: FONTS.bodyBold,
                  fontSize: 10.5,
                  letterSpacing: 1.6,
                  textTransform: "uppercase",
                  color: AMBER,
                }}
              >
                {showPast ? "Hide past calls ▴" : `Past calls (${past.length}) ▾`}
              </Text>
            </Pressable>

            {showPast ? (
              <View style={{ marginTop: 8, gap: 8 }}>
                {past.map((row) => (
                  <CallRow
                    key={row.id}
                    row={row}
                    isPast
                    onJoin={() => row.teams_join_url && openExternal(row.teams_join_url)}
                    onAddToCalendar={() => row.ics_url && openExternal(row.ics_url)}
                    onMarkComplete={() => void onStatus(row.id, "completed")}
                    onMarkNoShow={() => void onStatus(row.id, "no_show")}
                    onCancel={() => void onStatus(row.id, "cancelled")}
                    onEdit={() => setEditCall(row)}
                    onLinkProgram={() => setLinkCall(row)}
                  />
                ))}
              </View>
            ) : null}
          </View>
        )}
      </View>

      <LogCallModal
        visible={logOpen}
        onClose={() => setLogOpen(false)}
        onSaved={() => {
          setLogOpen(false);
          void load();
        }}
      />

      <EditCallModal
        call={editCall}
        onClose={() => setEditCall(null)}
        onSaved={() => {
          setEditCall(null);
          void load();
        }}
      />

      <LinkProgramModal
        call={linkCall}
        onClose={() => setLinkCall(null)}
        onSaved={() => {
          setLinkCall(null);
          void load();
        }}
      />
    </ScrollView>
  );
}

// ─── Row ───────────────────────────────────────────────────────────────

function CallRow({
  row,
  isPast,
  onJoin,
  onAddToCalendar,
  onMarkComplete,
  onMarkNoShow,
  onCancel,
  onEdit,
  onLinkProgram,
}: {
  row: CoravuCallRow;
  isPast?: boolean;
  onJoin: () => void;
  onAddToCalendar: () => void;
  onMarkComplete: () => void;
  onMarkNoShow: () => void;
  onCancel: () => void;
  onEdit: () => void;
  onLinkProgram: () => void;
}) {
  const tone = STATUS_TONE[row.status];
  const tzAbbr = fmtTzAbbr(row.starts_at);
  return (
    <View
      style={{
        padding: 14,
        borderRadius: 6,
        backgroundColor: FOREST_MID,
        borderWidth: 1,
        borderColor: BORDER,
      }}
    >
      <View style={{ flexDirection: "row", flexWrap: "wrap", alignItems: "flex-start", gap: 14 }}>
        <View style={{ minWidth: 92 }}>
          <Text style={{ fontFamily: FONTS.displayMedium, fontSize: 20, color: LINEN }}>
            {fmtTime(row.starts_at)}
          </Text>
          <Text style={{ marginTop: 2, fontFamily: FONTS.bodyMedium, fontSize: 10.5, color: WM }}>
            {fmtShortDate(row.starts_at)}
            {tzAbbr ? ` · ${tzAbbr}` : ""}
          </Text>
        </View>

        <View style={{ flex: 1, minWidth: 180 }}>
          <Text style={{ fontFamily: FONTS.bodyMedium, fontSize: 13.5, color: LINEN }}>
            {row.attendee_name}
          </Text>
          <Text style={{ marginTop: 1, fontFamily: FONTS.body, fontSize: 11.5, color: WS }}>
            {row.attendee_email}
          </Text>
          {row.company_id && row.company_name ? (
            <Pressable
              onPress={() => router.push(`/admin/company/${row.company_id}` as Href)}
              style={[
                { marginTop: 4, alignSelf: "flex-start" } as ViewStyle,
                Platform.OS === "web" ? ({ cursor: "pointer" } as const) : null,
              ]}
            >
              <Text
                style={{
                  fontFamily: FONTS.bodyBold,
                  fontSize: 11,
                  color: AMBER,
                  textDecorationLine: "underline",
                }}
              >
                {row.company_name}
              </Text>
            </Pressable>
          ) : null}
          {row.related_program_name ? (
            <Text style={{ marginTop: 3, fontFamily: FONTS.body, fontSize: 11, color: WM }}>
              Program: {row.related_program_name}
            </Text>
          ) : null}
          {row.notes ? (
            <Text style={{ marginTop: 6, fontFamily: FONTS.body, fontSize: 11.5, color: WS, lineHeight: 16 }}>
              {row.notes}
            </Text>
          ) : null}
        </View>

        <View
          style={{
            paddingHorizontal: 8,
            paddingVertical: 3,
            borderRadius: 999,
            borderWidth: 1,
            borderColor: tone,
            backgroundColor: "rgba(255,255,255,0.04)",
          }}
        >
          <Text
            style={{
              fontFamily: FONTS.bodyBold,
              fontSize: 9,
              letterSpacing: 1.4,
              textTransform: "uppercase",
              color: tone,
            }}
          >
            {STATUS_LABEL[row.status]}
          </Text>
        </View>
      </View>

      <View style={{ marginTop: 12, flexDirection: "row", flexWrap: "wrap", gap: 6 }}>
        {row.teams_join_url ? (
          <RowAction label="Join Teams →" tint={AMBER} onPress={onJoin} />
        ) : null}
        {row.ics_url ? (
          <RowAction label="Add to my calendar" onPress={onAddToCalendar} />
        ) : null}
        {!isPast && row.status === "scheduled" ? (
          <>
            <RowAction label="Mark complete" onPress={onMarkComplete} />
            <RowAction label="Mark no-show" onPress={onMarkNoShow} />
            <RowAction label="Cancel" onPress={onCancel} />
          </>
        ) : null}
        <RowAction
          label={row.related_program_id ? "Change program" : "Link program"}
          onPress={onLinkProgram}
        />
        {isPast ? <RowAction label="Edit" onPress={onEdit} /> : null}
      </View>
      {row.related_program_id && row.related_program_name ? (
        <View style={{ marginTop: 8, flexDirection: "row" }}>
          <View
            style={{
              paddingHorizontal: 8,
              paddingVertical: 3,
              borderRadius: 999,
              borderWidth: 1,
              borderColor: AMBER,
              backgroundColor: "rgba(181,135,58,0.12)",
            }}
          >
            <Text
              style={{
                fontFamily: FONTS.bodyMedium,
                fontSize: 10.5,
                color: AMBER,
              }}
            >
              ▸ {row.related_program_name}
            </Text>
          </View>
        </View>
      ) : null}
      {row.outcome ? (
        <Text style={{ marginTop: 4, fontFamily: FONTS.bodyMedium, fontSize: 10.5, color: WM }}>
          Outcome: {outcomeLabel(row.outcome)}
        </Text>
      ) : null}
    </View>
  );
}

function RowAction({ label, tint, onPress }: { label: string; tint?: string; onPress: () => void }) {
  const variant = tint === RED ? "destructive" : tint === AMBER ? "filled" : "outline";
  return <AdminButton theme={THEME} label={label} variant={variant} size="sm" onPress={onPress} />;
}

// ─── Log a call modal ──────────────────────────────────────────────────

function LogCallModal({
  visible,
  onClose,
  onSaved,
}: {
  visible: boolean;
  onClose: () => void;
  onSaved: () => void;
}) {
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [startsAt, setStartsAt] = useState("");
  const [endsAt, setEndsAt] = useState("");
  const [teamsUrl, setTeamsUrl] = useState("");
  const [icsUrl, setIcsUrl] = useState("");
  const [notes, setNotes] = useState("");
  const [companyQuery, setCompanyQuery] = useState("");
  const [companies, setCompanies] = useState<AdminCompanyListRow[]>([]);
  const [companyId, setCompanyId] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  const reset = useCallback(() => {
    setName("");
    setEmail("");
    setStartsAt("");
    setEndsAt("");
    setTeamsUrl("");
    setIcsUrl("");
    setNotes("");
    setCompanyQuery("");
    setCompanyId(null);
    setBusy(false);
    setErr(null);
  }, []);

  useFocusEffect(
    useCallback(() => {
      if (!visible) return;
      void (async () => {
        try {
          setCompanies(await listAllCompanies());
        } catch {
          /* ignore */
        }
      })();
    }, [visible])
  );

  const filteredCompanies = useMemo(() => {
    const q = companyQuery.trim().toLowerCase();
    if (!q) return companies.slice(0, 6);
    return companies.filter((c) => c.name.toLowerCase().includes(q)).slice(0, 8);
  }, [companies, companyQuery]);

  async function onSave() {
    setErr(null);
    if (!name.trim() || !email.trim() || !startsAt.trim()) {
      setErr("Attendee name, email, and start time are required.");
      return;
    }
    const startsIso = parseLocalDateTime(startsAt);
    if (!startsIso) {
      setErr("Start time isn't a valid date/time.");
      return;
    }
    const endsIso = endsAt.trim() ? parseLocalDateTime(endsAt) : null;
    if (endsAt.trim() && !endsIso) {
      setErr("End time isn't a valid date/time.");
      return;
    }
    setBusy(true);
    const result = await createCall({
      attendee_name: name,
      attendee_email: email,
      starts_at: startsIso,
      ends_at: endsIso,
      company_id: companyId,
      teams_join_url: teamsUrl.trim() || null,
      ics_url: icsUrl.trim() || null,
      notes: notes.trim() || null,
    });
    setBusy(false);
    if (!result) {
      setErr("Could not save the call. Check the fields and try again.");
      return;
    }
    reset();
    onSaved();
  }

  if (!visible) return null;

  return (
    <Modal visible={visible} transparent animationType="fade" onRequestClose={onClose}>
      <View style={{ flex: 1, backgroundColor: "rgba(0,0,0,0.65)", justifyContent: "center", paddingHorizontal: 16 }}>
        <ScrollView contentContainerStyle={{ paddingVertical: 24 }} keyboardShouldPersistTaps="handled">
          <View
            style={{
              maxWidth: 560,
              width: "100%",
              alignSelf: "center",
              backgroundColor: FOREST_MID,
              borderRadius: 8,
              borderWidth: 1,
              borderColor: BORDER,
              padding: 22,
            }}
          >
            <Text style={{ fontFamily: FONTS.displayMedium, fontSize: 22, color: LINEN }}>
              Log a call
            </Text>
            <Text style={{ marginTop: 4, fontFamily: FONTS.body, fontSize: 12, color: WM }}>
              Manual entry until the Bookings webhook ingests automatically.
            </Text>

            <ModalField label="Attendee name *" value={name} onChange={setName} placeholder="Jane Smith" />
            <ModalField
              label="Attendee email *"
              value={email}
              onChange={setEmail}
              placeholder="jane@company.com"
              autoCapitalize="none"
              keyboardType="email-address"
            />

            <View style={{ marginTop: 12 }}>
              <Text
                style={{
                  fontFamily: FONTS.bodyBold,
                  fontSize: 9.5,
                  letterSpacing: 1.6,
                  textTransform: "uppercase",
                  color: WM,
                }}
              >
                Company (optional)
              </Text>
              <TextInput
                value={companyQuery}
                onChangeText={(v) => {
                  setCompanyQuery(v);
                  setCompanyId(null);
                }}
                placeholder="Search…"
                placeholderTextColor={WL}
                style={
                  {
                    marginTop: 6,
                    backgroundColor: BLACK,
                    borderWidth: 1,
                    borderColor: BORDER,
                    borderRadius: 4,
                    paddingHorizontal: 12,
                    paddingVertical: 10,
                    color: W,
                    fontFamily: FONTS.body,
                    fontSize: 13,
                    ...(Platform.OS === "web" ? ({ outlineStyle: "none" } as object) : {}),
                  } as unknown as TextStyle
                }
              />
              {companyId ? (
                <Text style={{ marginTop: 4, fontFamily: FONTS.body, fontSize: 11, color: WM }}>
                  Linked: {companies.find((c) => c.id === companyId)?.name ?? "—"}
                </Text>
              ) : (
                <View style={{ marginTop: 6, flexDirection: "row", flexWrap: "wrap", gap: 6 }}>
                  {filteredCompanies.map((c) => (
                    <Pressable
                      key={c.id}
                      onPress={() => {
                        setCompanyId(c.id);
                        setCompanyQuery(c.name);
                      }}
                      style={[
                        {
                          paddingHorizontal: 9,
                          paddingVertical: 4,
                          borderRadius: 999,
                          borderWidth: 1,
                          borderColor: BORDER,
                          backgroundColor: "rgba(255,255,255,0.02)",
                        } as ViewStyle,
                        Platform.OS === "web" ? ({ cursor: "pointer" } as const) : null,
                      ]}
                    >
                      <Text style={{ fontFamily: FONTS.bodyMedium, fontSize: 11, color: WS }}>{c.name}</Text>
                    </Pressable>
                  ))}
                </View>
              )}
            </View>

            <ModalField
              label="Start time * (YYYY-MM-DD HH:mm, Eastern)"
              value={startsAt}
              onChange={setStartsAt}
              placeholder="2026-05-08 14:00"
              autoCapitalize="none"
            />
            <ModalField
              label="End time (optional)"
              value={endsAt}
              onChange={setEndsAt}
              placeholder="2026-05-08 15:00"
              autoCapitalize="none"
            />
            <ModalField
              label="Teams join URL (paste from Outlook)"
              value={teamsUrl}
              onChange={setTeamsUrl}
              placeholder="https://teams.microsoft.com/l/meetup-join/…"
              autoCapitalize="none"
            />
            <ModalField
              label=".ics URL (optional)"
              value={icsUrl}
              onChange={setIcsUrl}
              placeholder="https://…/event.ics"
              autoCapitalize="none"
            />
            <ModalField
              label="Notes (optional)"
              value={notes}
              onChange={setNotes}
              placeholder="Anything Tyler/Luke should know going in."
              multiline
            />

            {err ? (
              <Text style={{ marginTop: 12, fontFamily: FONTS.bodyMedium, fontSize: 12, color: RED }}>{err}</Text>
            ) : null}

            <View style={{ marginTop: 16, flexDirection: "row", gap: 10 }}>
              <Pressable
                onPress={() => void onSave()}
                disabled={busy}
                style={[
                  {
                    flex: 1,
                    paddingVertical: 11,
                    borderRadius: 3,
                    backgroundColor: AMBER,
                    alignItems: "center",
                    opacity: busy ? 0.5 : 1,
                  } as ViewStyle,
                  Platform.OS === "web" && !busy ? ({ cursor: "pointer" } as const) : null,
                ]}
              >
                {busy ? (
                  <ActivityIndicator size="small" color={FOREST} />
                ) : (
                  <Text
                    style={{
                      fontFamily: FONTS.bodyBold,
                      fontSize: 11.5,
                      letterSpacing: 1.4,
                      textTransform: "uppercase",
                      color: FOREST,
                    }}
                  >
                    Save call
                  </Text>
                )}
              </Pressable>
              <Pressable
                onPress={() => {
                  reset();
                  onClose();
                }}
                style={[
                  {
                    paddingHorizontal: 18,
                    paddingVertical: 11,
                    borderRadius: 3,
                    borderWidth: 1,
                    borderColor: BORDER,
                  } as ViewStyle,
                  Platform.OS === "web" ? ({ cursor: "pointer" } as const) : null,
                ]}
              >
                <Text style={{ fontFamily: FONTS.bodyMedium, fontSize: 12, color: WS }}>Cancel</Text>
              </Pressable>
            </View>
          </View>
        </ScrollView>
      </View>
    </Modal>
  );
}

function ModalField({
  label,
  value,
  onChange,
  placeholder,
  autoCapitalize,
  keyboardType,
  multiline,
}: {
  label: string;
  value: string;
  onChange: (v: string) => void;
  placeholder?: string;
  autoCapitalize?: "none" | "sentences" | "words" | "characters";
  keyboardType?: "default" | "email-address" | "numeric" | "phone-pad";
  multiline?: boolean;
}) {
  return (
    <View style={{ marginTop: 12 }}>
      <AdminInput
        theme={THEME}
        label={label}
        value={value}
        onChangeText={onChange}
        placeholder={placeholder}
        autoCapitalize={autoCapitalize}
        keyboardType={keyboardType === "phone-pad" ? "numeric" : keyboardType}
        multiline={multiline}
        minHeight={multiline ? 70 : undefined}
      />
    </View>
  );
}

/**
 * Parses a "YYYY-MM-DD HH:mm" or "YYYY-MM-DDTHH:mm" string as local time
 * and returns the ISO-8601 UTC representation. Returns null if the input
 * doesn't parse cleanly. Avoids `new Date(str)` because that does silent
 * UTC vs local detection that bites us on web.
 *
 * NOTE: still parses in the user's local TZ rather than Eastern. On the
 * to-do list — flag in DEVLOG. The display side is now Eastern-aware.
 */
function parseLocalDateTime(raw: string): string | null {
  const m = raw.trim().match(/^(\d{4})-(\d{2})-(\d{2})[ T](\d{2}):(\d{2})$/);
  if (!m) return null;
  const [, y, mo, d, h, mi] = m;
  const date = new Date(Number(y), Number(mo) - 1, Number(d), Number(h), Number(mi));
  if (Number.isNaN(date.getTime())) return null;
  return date.toISOString();
}

// ─── Edit call modal ───────────────────────────────────────────────────

function EditCallModal({
  call,
  onClose,
  onSaved,
}: {
  call: CoravuCallRow | null;
  onClose: () => void;
  onSaved: () => void;
}) {
  const [notes, setNotes] = useState("");
  const [outcome, setOutcome] = useState<CoravuCallOutcome | null>(null);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  useEffect(() => {
    if (call) {
      setNotes(call.notes ?? "");
      setOutcome(call.outcome ?? null);
      setErr(null);
    }
  }, [call]);

  async function onSave() {
    if (!call) return;
    setBusy(true);
    setErr(null);
    const ok = await updateCall(call.id, {
      notes: notes.trim() || null,
      outcome,
    });
    setBusy(false);
    if (!ok) {
      setErr("Couldn't save the call. Try again?");
      return;
    }
    onSaved();
  }

  if (!call) return null;
  return (
    <Modal visible transparent animationType="fade" onRequestClose={onClose}>
      <View style={{ flex: 1, backgroundColor: "rgba(0,0,0,0.65)", justifyContent: "center", paddingHorizontal: 16 }}>
        <ScrollView contentContainerStyle={{ paddingVertical: 24 }} keyboardShouldPersistTaps="handled">
          <View
            style={{
              maxWidth: 520,
              width: "100%",
              alignSelf: "center",
              backgroundColor: FOREST_MID,
              borderRadius: 8,
              borderWidth: 1,
              borderColor: BORDER,
              padding: 22,
            }}
          >
            <Text style={{ fontFamily: FONTS.displayMedium, fontSize: 22, color: LINEN }}>
              Edit call
            </Text>
            <Text style={{ marginTop: 4, fontFamily: FONTS.body, fontSize: 12, color: WM }}>
              {call.attendee_name} · {call.attendee_email}
            </Text>

            <Text style={{ marginTop: 14, fontFamily: FONTS.bodyBold, fontSize: 9.5, letterSpacing: 1.6, textTransform: "uppercase", color: WM }}>
              Outcome
            </Text>
            <View style={{ marginTop: 8, flexDirection: "row", flexWrap: "wrap", gap: 6 }}>
              <Pressable
                onPress={() => setOutcome(null)}
                style={[
                  {
                    paddingHorizontal: 10,
                    paddingVertical: 6,
                    borderRadius: 999,
                    borderWidth: 1,
                    borderColor: outcome === null ? AMBER : BORDER,
                    backgroundColor: outcome === null ? "rgba(181,135,58,0.12)" : "transparent",
                  } as ViewStyle,
                  Platform.OS === "web" ? ({ cursor: "pointer" } as const) : null,
                ]}
              >
                <Text style={{ fontFamily: FONTS.bodyMedium, fontSize: 11, color: outcome === null ? AMBER : WS }}>
                  Not set
                </Text>
              </Pressable>
              {OUTCOME_OPTIONS.map((opt) => {
                const sel = outcome === opt.value;
                return (
                  <Pressable
                    key={opt.value}
                    onPress={() => setOutcome(opt.value)}
                    style={[
                      {
                        paddingHorizontal: 10,
                        paddingVertical: 6,
                        borderRadius: 999,
                        borderWidth: 1,
                        borderColor: sel ? AMBER : BORDER,
                        backgroundColor: sel ? "rgba(181,135,58,0.12)" : "transparent",
                      } as ViewStyle,
                      Platform.OS === "web" ? ({ cursor: "pointer" } as const) : null,
                    ]}
                  >
                    <Text style={{ fontFamily: FONTS.bodyMedium, fontSize: 11, color: sel ? AMBER : WS }}>
                      {opt.label}
                    </Text>
                  </Pressable>
                );
              })}
            </View>

            <Text style={{ marginTop: 14, fontFamily: FONTS.bodyBold, fontSize: 9.5, letterSpacing: 1.6, textTransform: "uppercase", color: WM }}>
              Notes
            </Text>
            <TextInput
              value={notes}
              onChangeText={setNotes}
              placeholder="What happened on the call, who you spoke with, decisions made…"
              placeholderTextColor={WL}
              multiline
              style={
                {
                  marginTop: 6,
                  minHeight: 100,
                  backgroundColor: BLACK,
                  borderWidth: 1,
                  borderColor: BORDER,
                  borderRadius: 4,
                  paddingHorizontal: 12,
                  paddingVertical: 10,
                  color: W,
                  fontFamily: FONTS.body,
                  fontSize: 13,
                  textAlignVertical: "top",
                  ...(Platform.OS === "web" ? ({ outlineStyle: "none" } as object) : {}),
                } as unknown as TextStyle
              }
            />

            {err ? (
              <Text style={{ marginTop: 10, fontFamily: FONTS.bodyMedium, fontSize: 12, color: RED }}>
                {err}
              </Text>
            ) : null}

            <View style={{ marginTop: 16, flexDirection: "row", gap: 10 }}>
              <Pressable
                onPress={() => void onSave()}
                disabled={busy}
                style={[
                  {
                    flex: 1,
                    paddingVertical: 10,
                    borderRadius: 3,
                    backgroundColor: AMBER,
                    alignItems: "center",
                    opacity: busy ? 0.5 : 1,
                  } as ViewStyle,
                  Platform.OS === "web" && !busy ? ({ cursor: "pointer" } as const) : null,
                ]}
              >
                {busy ? (
                  <ActivityIndicator size="small" color={FOREST} />
                ) : (
                  <Text
                    style={{
                      fontFamily: FONTS.bodyBold,
                      fontSize: 11.5,
                      letterSpacing: 1.4,
                      textTransform: "uppercase",
                      color: FOREST,
                    }}
                  >
                    Save
                  </Text>
                )}
              </Pressable>
              <Pressable
                onPress={onClose}
                style={[
                  {
                    paddingHorizontal: 18,
                    paddingVertical: 10,
                    borderRadius: 3,
                    borderWidth: 1,
                    borderColor: BORDER,
                  } as ViewStyle,
                  Platform.OS === "web" ? ({ cursor: "pointer" } as const) : null,
                ]}
              >
                <Text style={{ fontFamily: FONTS.bodyMedium, fontSize: 12, color: WS }}>Cancel</Text>
              </Pressable>
            </View>
          </View>
        </ScrollView>
      </View>
    </Modal>
  );
}

// ─── Link program modal ───────────────────────────────────────────────

function LinkProgramModal({
  call,
  onClose,
  onSaved,
}: {
  call: CoravuCallRow | null;
  onClose: () => void;
  onSaved: () => void;
}) {
  const [comps, setComps] = useState<AdminCompetitionListRow[]>([]);
  const [search, setSearch] = useState("");
  const [busyId, setBusyId] = useState<string | null>(null);
  const [err, setErr] = useState<string | null>(null);

  useEffect(() => {
    if (!call) return;
    setSearch("");
    setErr(null);
    void (async () => {
      try {
        setComps(await listAllCompetitions());
      } catch (e) {
        setErr(e instanceof Error ? e.message : "Failed to load competitions");
      }
    })();
  }, [call]);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return comps;
    return comps.filter((c) => `${c.name} ${c.company_name ?? ""}`.toLowerCase().includes(q));
  }, [comps, search]);

  async function pick(id: string | null) {
    if (!call) return;
    setBusyId(id ?? "__clear");
    setErr(null);
    const ok = await linkCallToProgram(call.id, id);
    setBusyId(null);
    if (!ok) {
      setErr("Couldn't link the program. Try again?");
      return;
    }
    onSaved();
  }

  if (!call) return null;
  return (
    <Modal visible transparent animationType="fade" onRequestClose={onClose}>
      <View style={{ flex: 1, backgroundColor: "rgba(0,0,0,0.65)", justifyContent: "center", paddingHorizontal: 16 }}>
        <View
          style={{
            maxWidth: 560,
            width: "100%",
            alignSelf: "center",
            backgroundColor: FOREST_MID,
            borderRadius: 8,
            borderWidth: 1,
            borderColor: BORDER,
            padding: 22,
            maxHeight: Platform.OS === "web" ? ("82vh" as unknown as number) : 720,
          }}
        >
          <Text style={{ fontFamily: FONTS.displayMedium, fontSize: 22, color: LINEN }}>
            Link program
          </Text>
          <Text style={{ marginTop: 4, fontFamily: FONTS.body, fontSize: 12, color: WM }}>
            Tag this call with the competition it generated. Helps the
            CRM connect calls → programs → outcomes.
          </Text>

          <TextInput
            value={search}
            onChangeText={setSearch}
            placeholder="Search by program name or company…"
            placeholderTextColor={WL}
            style={
              {
                marginTop: 12,
                backgroundColor: BLACK,
                borderWidth: 1,
                borderColor: BORDER,
                borderRadius: 4,
                paddingHorizontal: 12,
                paddingVertical: 9,
                color: W,
                fontFamily: FONTS.body,
                fontSize: 13,
                ...(Platform.OS === "web" ? ({ outlineStyle: "none" } as object) : {}),
              } as unknown as TextStyle
            }
          />

          {err ? (
            <Text style={{ marginTop: 10, fontFamily: FONTS.bodyMedium, fontSize: 12, color: RED }}>
              {err}
            </Text>
          ) : null}

          <ScrollView style={{ marginTop: 12, maxHeight: 320 }}>
            {call.related_program_id ? (
              <Pressable
                onPress={() => void pick(null)}
                disabled={busyId === "__clear"}
                style={[
                  {
                    paddingHorizontal: 12,
                    paddingVertical: 10,
                    borderRadius: 4,
                    borderWidth: 1,
                    borderColor: BORDER,
                    backgroundColor: "rgba(255,255,255,0.02)",
                    marginBottom: 6,
                  } as ViewStyle,
                  Platform.OS === "web" && busyId !== "__clear" ? ({ cursor: "pointer" } as const) : null,
                ]}
              >
                <Text style={{ fontFamily: FONTS.bodyMedium, fontSize: 12, color: WS }}>
                  {busyId === "__clear" ? "Unlinking…" : "Unlink current program"}
                </Text>
              </Pressable>
            ) : null}
            {filtered.length === 0 ? (
              <Text style={{ fontFamily: FONTS.body, fontSize: 12, color: WM }}>
                No competitions match.
              </Text>
            ) : (
              filtered.map((c, idx) => {
                const sel = call.related_program_id === c.id;
                return (
                  <Pressable
                    key={c.id}
                    onPress={() => void pick(c.id)}
                    disabled={busyId !== null}
                    style={[
                      {
                        paddingHorizontal: 12,
                        paddingVertical: 10,
                        borderTopWidth: idx === 0 ? 0 : 1,
                        borderTopColor: BORDER,
                        backgroundColor: sel ? "rgba(181,135,58,0.10)" : "transparent",
                      } as ViewStyle,
                      Platform.OS === "web" ? ({ cursor: "pointer" } as const) : null,
                    ]}
                  >
                    <Text style={{ fontFamily: FONTS.bodyMedium, fontSize: 12.5, color: sel ? AMBER : LINEN }}>
                      {c.name}
                    </Text>
                    {c.company_name ? (
                      <Text style={{ marginTop: 1, fontFamily: FONTS.body, fontSize: 11, color: WM }}>
                        {c.company_name}
                      </Text>
                    ) : null}
                  </Pressable>
                );
              })
            )}
          </ScrollView>

          <Pressable
            onPress={onClose}
            style={[
              { marginTop: 14, alignItems: "center", paddingVertical: 6 } as ViewStyle,
              Platform.OS === "web" ? ({ cursor: "pointer" } as const) : null,
            ]}
          >
            <Text style={{ fontFamily: FONTS.bodyMedium, fontSize: 12, color: WS }}>Close</Text>
          </Pressable>
        </View>
      </View>
    </Modal>
  );
}
